import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Checkbox, FormControlLabel, Grid, MenuItem, Select, TextField } from '@mui/material';

import { FormRow, ToolTipIcon, handlePositiveIntChange, preventMinus } from './AutoIncrementor.helper';
import SchedulingContext from '../../../SchedulingContext';
import LocalizationContext from '../../../../../localization/LocalizationContext';

export default function PONumberIncrementorFields({ item, setItem }) {
    const { properties } = useContext(SchedulingContext);
    const {
        general: { customer_po: poLocalization },
    } = useContext(LocalizationContext) ?? { general: { customer_po: 'PO Number' } };

    if (properties && !properties.canUseAutoIncrement?.poNumber) return null;

    const [isEnabled, setIsEnabled] = useState(false);

    useEffect(() => {
        if (!isEnabled) {
            setItem((i) => ({ ...i, poNumberParams: { start_number: null, prefix: null, suffix: null, amount: null } }));
        }
    }, [isEnabled]);

    const handleChange = (type) => (event) => {
        let val;

        switch (type) {
            case 'start_number':
                if (event.target.value === '') {
                    val = null;
                } else {
                    val = handlePositiveIntChange(event.target.value, () => {});
                }
                break;
            case 'padding_zeros':
            case 'prefix':
            case 'suffix':
            default:
                val = event.target.value;
        }
        const itemToSet = { ...item, poNumberParams: { ...item.poNumberParams, [type]: val } };

        setItem(itemToSet);
    };

    const EnabledCheckbox = useCallback(() => {
        return (
            <FormControlLabel //
                control={<Checkbox checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />}
                label={`Auto Increment ${poLocalization}`}
            />
        );
    }, [isEnabled]);

    if (!isEnabled) {
        return (
            <Grid item xs={12}>
                <EnabledCheckbox isEnabled={isEnabled} setIsEnabled={setIsEnabled} />
            </Grid>
        );
    }

    return (
        <>
            <Grid item xs={11}>
                <EnabledCheckbox isEnabled={isEnabled} setIsEnabled={setIsEnabled} />
            </Grid>
            <Grid item xs={1} sx={{ display: 'flex' }}>
                <ToolTipIcon poLocalizationLabel={poLocalization}></ToolTipIcon>
            </Grid>
            <Grid item xs={12}>
                <FormRow label={`Starting ${poLocalization} *`} fullWidth>
                    <TextField
                        name="start_number" //
                        variant="outlined"
                        type="number"
                        min={0}
                        value={item.poNumberParams.start_number}
                        disabled={!isEnabled}
                        onKeyDown={preventMinus}
                        onChange={handleChange('start_number')}
                    />
                </FormRow>
            </Grid>
            <Grid item xs={3}>
                <FormRow label="Padding Zeros">
                    <Select
                        value={item.poNumberParams?.padding_zeros} // Wrap
                        onChange={handleChange('padding_zeros')}
                        displayEmpty
                        disabled={!isEnabled}
                        inputProps={{ 'aria-label': 'Whether to Pad or Prepend' }}
                    >
                        {Array.from({ length: 13 }, (_, i) => (
                            <MenuItem key={i} value={i}>
                                {i}
                            </MenuItem>
                        ))}
                    </Select>
                </FormRow>
            </Grid>
            <Grid item xs={4}>
                <FormRow label="Prefix (Optional)" fullWidth>
                    <TextField
                        name="prefix" //
                        variant="outlined"
                        value={item.poNumberParams?.prefix}
                        disabled={!isEnabled}
                        onChange={handleChange('prefix')}
                    />
                </FormRow>
            </Grid>
            <Grid item xs={5}>
                <FormRow label="Suffix (Optional)" fullWidth>
                    <TextField
                        name="suffix" //
                        variant="outlined"
                        value={item.poNumberParams?.suffix}
                        disabled={!isEnabled}
                        onChange={handleChange('suffix')}
                    />
                </FormRow>
            </Grid>
            {item.po_number && (
                <Grid item xs={12}>
                    {' '}
                    <Alert severity="warning">
                        {poLocalization} is entered above. Auto generation will overwrite the above {poLocalization}
                    </Alert>
                </Grid>
            )}
        </>
    );
}
